import { useCallback, useState } from "react";
import { useParams } from "react-router";
import { useFormik } from "formik";
import _ from "lodash";

import { EditIcon } from "icons";

import Dialog from "components/Dialog";
import { MenuItem } from "components/Menu";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

import { TRANSACTION_VALIDATION_SCHEMA } from "pages/Deal/FinancingEvents/constants";
import { ETransactionDialogStatus } from "pages/Deal/FinancingEvents/constants";
import TransactionForm from "pages/Deal/FinancingEvents/TransactionModifyDialog/TransactionForm";
import { getTransactionTypeFromTransactionOrFormValues } from "pages/Deal/FinancingEvents/TransactionModifyDialog/TransactionForm/TransactionGeneralInfoForm/utils";
import {
  handleTransactionInvalidRequest,
  handleTransactionTypeChange,
} from "pages/Deal/FinancingEvents/utils";

import { ETransactionType } from "entities/Deal/constants";
import {
  financingProgramCreate,
  ITransactionValuesForm,
  transactionUpdate,
  useDealDetails,
  useTransactions,
} from "entities/Deal/sdk";

export interface ITransactionUpdateDialogButton {
  initialValues: ITransactionValuesForm;
  financingEventId: number;
  lastUpdatedInformation: string | null;
  transactionId: number;
  transactionIdentifier: string;
}

const TransactionUpdateDialogButton = ({
  initialValues,
  financingEventId,
  transactionId,
  transactionIdentifier,
  lastUpdatedInformation,
}: ITransactionUpdateDialogButton) => {
  const [transactionDialogStatus, setTransactionDialogStatus] =
    useState<ETransactionDialogStatus>(ETransactionDialogStatus.CLOSED);

  const [previousTransactionType, setPreviousTransactionType] =
    useState<ETransactionType | null>(
      getTransactionTypeFromTransactionOrFormValues({
        transactionInfo: initialValues,
      })
    );

  const params = useParams<{
    dealId: string;
  }>();
  const dealId = Number(params.dealId);

  const [showLoading, hideLoading] = useLoadingBackdrop();

  const { data: deal } = useDealDetails({ dealId });

  const { mutate: mutateTransactions } = useTransactions({
    dealId,
    financingEventId,
  });

  const updateTransaction = useCallback(
    (data: ITransactionValuesForm, formik) => {
      transactionUpdate({ dealId, data, financingEventId, transactionId })
        .then(() => {
          setTransactionDialogStatus(ETransactionDialogStatus.CLOSED);
          toast.successMessage("Transaction updated successfully!");
          mutateTransactions();
          formik.resetForm();
        })
        .catch(handleTransactionInvalidRequest);
    },
    [dealId, financingEventId, mutateTransactions, transactionId]
  );

  const handleUpdateSubmit = useCallback(
    async (data: ITransactionValuesForm, formik) => {
      if (!_.isNil(transactionId)) {
        showLoading();
        if (
          _.isNil(data.financing_program_id) &&
          !_.isNil(data.financing_program_name)
        ) {
          const response = await financingProgramCreate({
            data: { name: data.financing_program_name },
          });
          data.financing_program_id = response.id;
        }

        updateTransaction(data, formik);

        hideLoading();
      }
    },
    [hideLoading, showLoading, transactionId, updateTransaction]
  );

  const formik = useFormik({
    initialValues: { ...initialValues, capital_provider: deal?.company.name },
    validationSchema: TRANSACTION_VALIDATION_SCHEMA,
    enableReinitialize: true,
    onSubmit: handleUpdateSubmit,
  });

  const onDialogClose = useCallback(() => {
    setTransactionDialogStatus(ETransactionDialogStatus.CLOSED);
    formik.resetForm();

    setPreviousTransactionType(null);
  }, [formik, setPreviousTransactionType, setTransactionDialogStatus]);

  const resetOldTransactionType = useCallback(() => {
    if (
      !_.isNil(previousTransactionType) &&
      formik.values.transaction_type !== previousTransactionType
    ) {
      setPreviousTransactionType(
        formik.values.transaction_type as ETransactionType
      );

      handleTransactionTypeChange({
        transactionType: formik.values.transaction_type as ETransactionType,
        setFieldValue: formik.setFieldValue,
      });
    }
  }, [formik, previousTransactionType, setPreviousTransactionType]);

  const onUpdateButtonClickHandler = useCallback(() => {
    resetOldTransactionType();
    formik.submitForm();
  }, [formik, resetOldTransactionType]);

  return (
    <>
      <MenuItem
        onClick={() =>
          setTransactionDialogStatus(ETransactionDialogStatus.GENERAL_INFO)
        }
      >
        <Stack gap={1} flexDirection="row" alignItems="center">
          <EditIcon />
          <Text variant="text2">Edit general details</Text>
        </Stack>
      </MenuItem>
      <MenuItem
        onClick={() =>
          setTransactionDialogStatus(ETransactionDialogStatus.SPECIFIC_INFO)
        }
      >
        <Stack gap={1} flexDirection="row">
          <EditIcon />
          <Text variant="text2">Edit specific details</Text>
        </Stack>
      </MenuItem>

      <Dialog
        open={transactionDialogStatus !== ETransactionDialogStatus.CLOSED}
        onClose={onDialogClose}
        fullWidth
      >
        <TransactionForm
          formik={formik}
          transactionDialogStatus={transactionDialogStatus}
          previousTransactionType={previousTransactionType}
          onDialogClose={onDialogClose}
          onUpdateButtonClickHandler={onUpdateButtonClickHandler}
          lastUpdatedInformation={lastUpdatedInformation}
          transactionIdentifier={transactionIdentifier}
        />
      </Dialog>
    </>
  );
};

export default TransactionUpdateDialogButton;
